import React, { useCallback, useEffect, useState } from 'react';
import { Mail, RefreshCw, Send, Loader2, CheckCircle2, AlertTriangle, XCircle, ShieldCheck, ShieldOff, Inbox } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

const EVENT_CLS = {
  delivered: 'bg-emerald-100 text-emerald-800', opened: 'bg-sky-100 text-sky-800', clicks: 'bg-sky-100 text-sky-800', requests: 'bg-slate-100 text-slate-600',
  softBounces: 'bg-amber-100 text-amber-800', hardBounces: 'bg-red-100 text-red-700', blocked: 'bg-red-100 text-red-700', spam: 'bg-red-100 text-red-700', deferred: 'bg-amber-50 text-amber-700', invalid: 'bg-red-50 text-red-600',
};

function Stat({ label, value, tone = 'slate', testId }) {
  return (
    <div className={`rounded-xl border px-3 py-2 ${tone === 'red' ? 'border-red-200 bg-red-50' : tone === 'amber' ? 'border-amber-200 bg-amber-50' : tone === 'green' ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className="text-base font-bold text-slate-900">{value ?? '—'}</p>
    </div>
  );
}

/** Admin Email Health — live/dev mode, provider, Brevo quota + domain auth, last 20 deliveries, test send. */
export default function EmailHealthCard() {
  const [data, setData] = useState(null);
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try { setData(await apiCall('/api/admin/email/health')); } catch (e) { toast.error(e.message); setData({}); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    if (!to.trim()) return;
    setSending(true); setResult(null);
    try {
      const r = await apiCall('/api/admin/email/test', { method: 'POST', body: JSON.stringify({ to: to.trim() }) });
      setResult(r);
      (r.sent ? toast.success : toast.warning)(r.message);
      setTimeout(load, 4000);
    } catch (e) { toast.error(e.message); setResult({ success: false, message: e.message }); }
    setSending(false);
  };

  if (data === null) return <p className="text-sm text-slate-500 py-3">Checking email service…</p>;
  const b = data.brevo;
  const live = data.mode === 'live';
  const domainOk = b?.domains?.some(d => d.authenticated);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="email-health-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1.5"><Mail className="w-4 h-4 text-indigo-500" /> Email Health</p>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${live ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-testid="email-mode-badge">
            {live ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {live ? 'LIVE — emails are being sent' : `DEV MODE — emails only logged (${data.dev_mode_reason})`}
          </span>
          <button onClick={load} className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid="email-health-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <Stat label="Provider" value={(data.providers || []).find(p => p.configured)?.name || 'none'} testId="email-provider" />
        <Stat label="Sender" value={<span className="text-xs break-all">{data.sender?.email}</span>} tone={b?.sender_verified ? 'green' : 'amber'} testId="email-sender" />
        <Stat label="Daily quota left" value={b?.plan?.credits_remaining != null ? `${b.plan.credits_remaining} (${b.plan.type})` : '—'} tone={b?.plan?.credits_remaining != null && b.plan.credits_remaining < 50 ? 'amber' : 'slate'} testId="email-quota" />
        <Stat label="Domain auth" value={<span className="inline-flex items-center gap-1 text-sm">{domainOk ? <ShieldCheck className="w-4 h-4 text-emerald-600" /> : <ShieldOff className="w-4 h-4 text-amber-600" />}{domainOk ? 'SPF/DKIM ok' : 'Not authenticated'}</span>} tone={domainOk ? 'green' : 'amber'} testId="email-domain" />
        <Stat label="30-day bounces" value={b?.last_30_days ? `${(b.last_30_days.softBounces || 0) + (b.last_30_days.hardBounces || 0)} / ${b.last_30_days.requests || 0} sent` : '—'} tone={b?.last_30_days && (b.last_30_days.softBounces + b.last_30_days.hardBounces) > 0 ? 'amber' : 'slate'} testId="email-bounces" />
      </div>

      {!domainOk && b && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3" data-testid="email-domain-warning">
          <b>{b.domains?.[0]?.domain || 'Your sending domain'}</b> is not authenticated in Brevo (SPF/DKIM/DMARC). Mail is accepted but may land in spam. Fix in Brevo → Senders, Domains &amp; Dedicated IPs → Domains → Authenticate.
        </p>
      )}
      {String(data.debug_flag).toLowerCase() === 'true' && live && (
        <p className="text-[11px] text-slate-500 mb-3">DEBUG is on for the API but no longer affects email — codes are only emailed, never shown on screen.</p>
      )}

      <div className="flex flex-col md:flex-row md:items-end gap-2 mb-4">
        <div className="flex-1">
          <label className="text-[11px] font-semibold text-slate-500 uppercase">Send a test email</label>
          <input value={to} onChange={e => setTo(e.target.value)} type="email" placeholder="you@yourcompany.com"
            className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/30" data-testid="email-test-to" />
        </div>
        <button onClick={sendTest} disabled={sending || !to.trim()} className="inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50" data-testid="email-test-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
        </button>
      </div>
      {result && (
        <p className={`text-xs rounded-lg px-3 py-2 mb-4 border ${result.sent ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid="email-test-result">
          {result.sent ? <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> : <XCircle className="w-3.5 h-3.5 inline mr-1" />}{result.message}{result.provider ? ` · via ${result.provider}` : ''}
        </p>
      )}

      <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1.5 flex items-center gap-1"><Inbox className="w-3.5 h-3.5" /> Last {b?.events?.length || 0} delivery events (Brevo)</p>
      {!b ? <p className="text-xs text-slate-400">Brevo key not configured.</p> : b.error ? <p className="text-xs text-red-600">Brevo error: {b.error}</p> : b.events.length === 0 ? <p className="text-xs text-slate-400">No emails handed to Brevo in the last 30 days.</p> : (
        <div className="overflow-x-auto max-h-64 overflow-y-auto">
          <table className="w-full text-xs" data-testid="email-events-table">
            <thead className="text-slate-400 sticky top-0 bg-white"><tr><th className="text-left py-1 font-medium">When</th><th className="text-left font-medium">Event</th><th className="text-left font-medium">To</th><th className="text-left font-medium">Subject</th><th className="text-left font-medium">Reason</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {b.events.map((e, i) => (
                <tr key={`${e.message_id}-${e.event}-${i}`} data-testid={`email-event-${i}`}>
                  <td className="py-1.5 text-slate-500 whitespace-nowrap">{new Date(e.date).toLocaleString()}</td>
                  <td><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${EVENT_CLS[e.event] || 'bg-slate-100 text-slate-600'}`}>{e.event}</span></td>
                  <td className="text-slate-700">{e.email}</td>
                  <td className="text-slate-600 truncate max-w-[260px]">{e.subject}</td>
                  <td className="text-slate-500">{e.reason || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.digest_skips?.total > 0 && (
        <p className="text-[11px] text-slate-500 mt-3" data-testid="email-skips-note">{data.digest_skips.total} digest email{data.digest_skips.total === 1 ? '' : 's'} skipped so far (placeholder / unverified addresses) — quota protected.</p>
      )}
    </div>
  );
}
